import React from 'react';
import { SquareSplitHorizontalSvg } from '../../../assets/img/redesign/svgPaths';

export const getInboxDocumentMetadata = (record) => {
  const parts = record.packet_upload?.parts;
  const part = parts?.length === 1 ? parts[0] : null;
  return {
    documentTitle: part?.title ?? record.document_title,
    documentCategory: part?.category ?? record.document_category,
  };
};

// Adapt a part to the ordinary document presenter. No packet-specific icon set.
const getPartStatus = (part, transmission, documentStatus) => {
  const sftp = part.destination === 'sftp' || part.outcome === 'sftp_delivered';
  const destination = sftp ? 'SFTP' : 'EMR';
  const packetLink = transmission.packet_upload?.chart_link;
  const context = { emr_patient_chart_link: transmission.emr_patient_chart_link || (/^https?:\/\//i.test(packetLink || '') ? packetLink : null) };
  const present = (fields) => documentStatus({ ...context, ...fields });
  if (part.outcome === 'chart_delivered') {
    return { ...present({ auto_upload_created: true }), message: 'Uploaded to chart' };
  }
  if (part.outcome === 'sftp_delivered') {
    if (part.sftp_only) return present({ sftp_category_delivery_outcome: 'delivered' });
    if (part.sftp_reason === 'patient_not_found') return present({ routed_to_sftp: true });
    return present({ sftp_fallback_outcome: 'delivered', sftp_fallback_reason: part.sftp_reason });
  }
  if (['failed', 'uncertain'].includes(part.outcome)) {
    const status = present(sftp
      ? { sftp_category_delivery_outcome: 'failed' }
      : { emr_import_error: part.error_message || 'Document upload failed' });
    return sftp && !part.sftp_only
      ? { ...status, message: `Document upload to ${destination} failed. Contact support for help.` }
      : status;
  }
  if (['blocked', 'retryable'].includes(part.outcome)) {
    const status = present(sftp ? { sftp_category_delivery_outcome: 'retryable' } : { emr_import_error: 'File or destination unavailable' });
    if (sftp && part.sftp_only) return status;
    return { ...status, message: sftp ? 'File or destination unavailable. Delivery to SFTP will retry automatically.' : 'File or destination unavailable.' };
  }
  if (sftp) {
    const status = present({ sftp_category_delivery_outcome: 'in_progress' });
    return part.sftp_only ? status : { ...status, message: 'This document is being uploaded to SFTP.' };
  }
  return present({ auto_upload_in_progress: true, auto_upload_scheduled: transmission.auto_upload_scheduled });
};

export const getPacketUploadStatus = (packet, transmission, documentStatus) => {
  if (!Array.isArray(packet?.parts) || !packet.parts.length) return null;
  if (packet.parts.length === 1) return getPartStatus(packet.parts[0], transmission, documentStatus);

  const parts = packet.parts.map((part) => {
    const status = getPartStatus(part, transmission, documentStatus);
    const title = part.title || part.category || 'Document';
    const pages = part.pages?.length ? `${part.pages.length === 1 ? 'Page' : 'Pages'} ${part.pages.join(', ')}` : '';
    const detail = [part.category, pages].filter(Boolean).join(' · ');
    const reason = { patient_not_found: 'patient chart not found', missing_date_of_service: 'missing date of service' }[part.sftp_reason];
    let summary = status.label;
    if (part.outcome === 'sftp_delivered' && reason) summary += ` — ${reason}`;
    if (['blocked', 'retryable'].includes(part.outcome)) summary = part.destination === 'sftp' ? 'File or destination unavailable — retrying SFTP delivery' : 'File or destination unavailable';
    if (['failed', 'uncertain'].includes(part.outcome) && part.error_message && part.error_message !== 'Document upload failed') {
      summary += ` — ${status.message}`;
    }
    return {
      ...part, title, detail, summary, status,
    };
  });
  const label = `${packet.delivered}/${packet.total} parts delivered`;
  const complete = packet.delivered === packet.total;
  const needsAttention = parts.some(({ status }) => ['warning', 'error'].includes(status.severity));
  let severity = complete ? 'success' : 'info';
  if (needsAttention) severity = 'warning';
  return {
    statusKey: 'packet_upload',
    severity,
    label,
    parts,
    message: [label, ...parts.map((part) => `${[part.title, part.detail].filter(Boolean).join(' · ')}: ${part.summary}`)].join('\n'),
    iconNode: React.cloneElement(SquareSplitHorizontalSvg, {
      width: 22,
      height: 22,
      role: 'img',
      'aria-label': 'Split document',
      style: { display: 'block', color: severity === 'success' ? '#52c41a' : '#d48806' },
    }),
    link: /^https?:\/\//i.test(packet.chart_link || '') ? packet.chart_link : null,
  };
};

export default getPacketUploadStatus;
